import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

export const ServiceWorkerStatus = () => {
  const [swStatus, setSwStatus] = useState<string>('Checking...');
  const [registrations, setRegistrations] = useState<ServiceWorkerRegistration[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    checkServiceWorkerStatus();
  }, []);

  const checkServiceWorkerStatus = async () => {
    if (!('serviceWorker' in navigator)) {
      setSwStatus('Service Worker not supported');
      return;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      setRegistrations([...registrations]);
      
      if (registrations.length === 0) {
        setSwStatus('No Service Worker registered');
        return;
      }

      const registration = registrations[0];
      const sw = registration.active;
      
      if (sw) {
        setSwStatus(`Active: ${sw.scriptURL} (State: ${sw.state})`);
      } else if (registration.installing) {
        setSwStatus(`Installing: ${registration.installing.scriptURL}`);
      } else if (registration.waiting) {
        setSwStatus(`Waiting: ${registration.waiting.scriptURL}`);
      } else {
        setSwStatus('Service Worker in unknown state');
      }
    } catch (error) {
      setSwStatus(`Error: ${error}`);
    }
  };

  const unregisterAll = async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
      
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(c => caches.delete(c)));
      
      setSwStatus('All Service Workers unregistered and caches cleared');
      setRegistrations([]);
      
      // Reload page after clearing
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      setSwStatus(`Error clearing: ${error}`);
    }
  };

  const forceUpdate = async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        await registrations[0].update();
        setSwStatus('Service Worker update forced');
        setTimeout(() => checkServiceWorkerStatus(), 1000);
      }
    } catch (error) {
      setSwStatus(`Error updating: ${error}`);
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-[100]">
      <Button 
        size="sm" 
        variant="outline"
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-2"
      >
        SW Status
      </Button>
      
      {isExpanded && (
        <div className="p-4 border rounded-lg bg-card max-w-sm">
          <h3 className="font-semibold mb-2">Service Worker Status</h3>
          <p className="text-sm text-muted-foreground mb-4">{swStatus}</p>
          
          {registrations.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium">Registrations:</p>
              {registrations.map((reg, i) => (
                <div key={i} className="text-xs text-muted-foreground">
                  Scope: {reg.scope}
                </div>
              ))}
            </div>
          )}
          
          <div className="space-x-2 mb-4">
            <Button size="sm" onClick={checkServiceWorkerStatus}>
              Refresh Status
            </Button>
            <Button size="sm" variant="outline" onClick={forceUpdate}>
              Force Update
            </Button>
            <Button size="sm" variant="destructive" onClick={unregisterAll}>
              Unregister & Clear Caches
            </Button>
          </div>
          
          <div className="mt-4 p-2 bg-muted rounded text-xs">
            <p className="font-medium">Manual DevTools Commands:</p>
            <code>navigator.serviceWorker.getRegistrations().then(rs=&gt;rs.forEach(r=&gt;r.unregister()));</code>
            <br/>
            <code>caches.keys().then(k=&gt;k.forEach(c=&gt;caches.delete(c)));</code>
          </div>
        </div>
      )}
    </div>
  );
};